import { Link } from '@inertiajs/react'
import { ChevronDown, Settings2, Unplug } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useBasePath } from '@/lib/paths'
import { cn } from '@/lib/utils'

type SettingsSection = 'general' | 'integrations'

type SettingsNavigationProps = {
  active: SettingsSection
}

const items = [
  { key: 'general', label: 'General', icon: Settings2, path: '/settings' },
  { key: 'integrations', label: 'AI & integrations', icon: Unplug, path: '/settings/integrations' },
] as const

export function SettingsNavigation({ active }: SettingsNavigationProps) {
  const basePath = useBasePath()

  return (
    <nav aria-label="Settings" className="flex flex-col gap-1">
      <p className="mb-1.5 text-sm font-semibold text-foreground">Settings</p>
      {items.map((item) => {
        const isActive = item.key === active

        return (
          <Link
            key={item.key}
            href={`${basePath}${item.path}`}
            aria-current={isActive ? 'page' : undefined}
            className={cn(
              'flex h-8 items-center gap-2.5 rounded-md px-2 text-sm transition-colors',
              'outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isActive
                ? 'bg-muted font-medium text-foreground'
                : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
            )}
          >
            <item.icon className="size-4" aria-hidden="true" />
            {item.label}
          </Link>
        )
      })}
    </nav>
  )
}

export function SettingsMenuButton({ active }: SettingsNavigationProps) {
  const basePath = useBasePath()
  const current = items.find((item) => item.key === active)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 gap-1.5">
          {current?.label ?? 'Settings'}
          <ChevronDown className="size-4 text-muted-foreground" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {items.map((item) => (
          <DropdownMenuItem key={item.key} asChild>
            <Link href={`${basePath}${item.path}`}>
              <item.icon className="size-4" aria-hidden="true" />
              {item.label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
